import { useState } from 'react';

/**
 * In-app "contact us" modal for sales-led tenants (Phase C-3, locked decision
 * Q-E). No external dependency (no mailto, no Typeform) — shows a copyable
 * contact address and guidance. Tenant-side light styling (NOT the Indigo-Deep
 * platform skin); reuses the same modal shell shape as ReasonModal.
 *
 * Two intents (ADR-0032 D9):
 *   - `upgrade` — a real upsell (an Enterprise-required plan gate, a trial
 *     converting). "Upgrade your LumoHR subscription".
 *   - `billing` — an existing customer renewing, paying an invoice, or
 *     recovering from past_due/read_only/cancelled. Telling a paying customer
 *     to "upgrade" when they owe a renewal is wrong copy, not a nuance.
 */

/**
 * THE one billing contact address (ADR-0032 D9, decided 2026-09-13). Previously
 * a hard-coded interim personal address was shown to every Enterprise tenant.
 * Support (suspended accounts, legal requests) stays hello@lumohr.com — a
 * different mailbox for a different job.
 */
export const BILLING_CONTACT_EMAIL = 'billing@lumohr.com';

export type ContactIntent = 'upgrade' | 'billing';

const COPY: Record<ContactIntent, { title: string; body: string; footer: string }> = {
  upgrade: {
    title: 'Upgrade your LumoHR subscription',
    body: "To upgrade your plan, please contact us. We'll get back to you within one business day to discuss your subscription options.",
    footer: "When you reach out, please mention that you'd like to upgrade your LumoHR subscription. Our team will guide you through the process.",
  },
  billing: {
    title: 'Billing and renewals',
    body: "To renew your subscription, pay an invoice, or change your contract terms, contact our billing team. We'll get back to you within one business day.",
    footer: 'When you reach out, please include your organization name and, if you have one, your invoice number.',
  },
};

export function UpgradeContactModal({
  open,
  onClose,
  organizationName,
  intent = 'upgrade',
}: {
  open: boolean;
  onClose: () => void;
  organizationName?: string;
  intent?: ContactIntent;
}): JSX.Element | null {
  const [copied, setCopied] = useState(false);

  if (!open) return null;

  const copy = COPY[intent];

  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(BILLING_CONTACT_EMAIL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable (insecure context / permissions) — the user
      // can still select-copy the address from the displayed field.
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="upgrade-contact-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg p-6 max-w-md w-full mx-4 border shadow-lg text-gray-900"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="upgrade-contact-title" className="text-xl font-semibold mb-2">
          {copy.title}
        </h2>
        <p className="text-sm text-gray-700 mb-4">{copy.body}</p>

        <div className="mb-3">
          <span className="block text-sm font-medium text-gray-700 mb-1">Email</span>
          <div className="flex items-center gap-2 border rounded-md p-2 bg-gray-50">
            <code className="flex-1 text-sm">{BILLING_CONTACT_EMAIL}</code>
            <button
              type="button"
              onClick={handleCopy}
              className="text-xs text-indigo-primary hover:underline"
              aria-label="Copy email address to clipboard"
            >
              {copied ? '✓ Copied' : 'Copy'}
            </button>
          </div>
        </div>

        {organizationName ? (
          <div className="mb-4">
            <span className="block text-sm font-medium text-gray-700 mb-1">Organization</span>
            <div className="border rounded-md p-2 bg-gray-50 text-sm">{organizationName}</div>
          </div>
        ) : null}

        <p className="text-sm text-gray-600 mb-4">{copy.footer}</p>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-indigo-primary text-white rounded-md font-medium hover:opacity-90"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
